import { STATUS_STYLES, PRIORITY_STYLES, fmtDate, fmtDateTime, fmtQty } from "@/lib/constants";
import { useRef, useEffect } from "react";
import { stageViews, currentStageLabel } from "@/lib/stages";
import { isOverdue } from "@/lib/batches";
import { StageCards } from "@/components/stages/StageCards";
import { OrderFlowBar } from "@/components/WipPipeline";
import { motion, AnimatePresence } from "framer-motion";
import { useT, useTn } from "@/lib/i18n";
import { TranslatedName } from "@/components/TranslatedName";

const SLIDE = { height: { duration: 0.32, ease: [0.4, 0, 0.2, 1] }, opacity: { duration: 0.22 } };
import { OperatorChips } from "@/components/OperatorChips";
import { SourceBadge } from "@/components/workorders/SourceBadge";
import { BomRevisionBanner, BomAckBanner } from "@/components/orders/BomRevisionBanner";
import { Pencil, Trash2, Clock, AlertTriangle, Wand2, ChevronDown, ChevronRight, ShoppingBag } from "lucide-react";

const COLS = 10;

function StageCell({ order: o }) {
  const tn = useTn();
  const views = stageViews(o);
  if (!views.length) return <td className="px-4 py-3 text-[11px] text-slate-300">—</td>;
  const label = currentStageLabel(o);
  const finished = views[views.length - 1].done;
  return (
    <td className="px-4 py-3" data-testid={`stage-cell-${o.order_code}`}>
      <div className="flex items-center gap-1 mb-1">
        {views.map((v, i) => {
          let cls = "bg-slate-200";
          if (v.isDone) cls = "bg-emerald-500";
          else if (v.isActive || v.available > 0) cls = v.isOsp ? "bg-orange-500" : "bg-blue-500";
          return <span key={`${v.name}-${i}`} title={`${v.name} · ${fmtQty(v.done, o.unit)} / ${fmtQty(o.target_quantity, o.unit)}`} className={`h-1.5 w-5 rounded-full ${cls}`} />;
        })}
      </div>
      <p className="text-[10px] text-slate-500 font-mono-data truncate max-w-[150px]">{tn(label)}{finished > 0 && label !== "All Stages Done" ? ` · ${fmtQty(finished, o.unit)} finished` : ""}</p>
    </td>
  );
}

function ProgressCell({ order: o }) {
  const produced = o.produced_quantity || 0;
  const pct = o.target_quantity > 0 ? Math.min(100, Math.round((produced / o.target_quantity) * 100)) : 0;
  return (
    <td className="px-4 py-3" data-testid={`progress-cell-${o.order_code}`}>
      <div className="flex items-center gap-2 min-w-[110px]">
        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : pct > 0 ? "bg-emerald-500" : "bg-slate-200"}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[11px] font-mono-data text-slate-500 whitespace-nowrap">{pct}%</span>
      </div>
      <p className="text-[10px] text-slate-400 font-mono-data mt-0.5">{fmtQty(produced, o.unit)} / {fmtQty(o.target_quantity, o.unit)} finished</p>
    </td>
  );
}

// Separate clickable zone: clicks on any action button must NOT bubble up to the
// row's expand/collapse handler (the whole row is clickable).
function RowActions({ order: o, editable, expanded, onToggle, onEdit, onDelete, onSuggestFix, hideEdit = false }) {
  const hasStages = (o.stages || []).length > 0;
  return (
    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
      {hasStages && (
        <button onClick={() => onToggle(o.id)} className={`h-8 px-2 rounded-md flex items-center gap-1 text-[11px] font-medium transition-colors ${expanded ? "bg-indigo-100 text-indigo-800" : "text-indigo-600 hover:bg-indigo-100 hover:text-indigo-800"}`} data-testid={`toggle-stages-${o.order_code}`} title="Show stage-gate cards" aria-expanded={expanded}>
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Stages
        </button>
      )}
      {editable ? (
        <>
          {o.has_conflict && onSuggestFix && (
            <button onClick={() => onSuggestFix(o)} className="h-8 px-2 rounded-md flex items-center gap-1 text-amber-600 hover:bg-amber-100 hover:text-amber-800 transition-colors text-[11px] font-medium" data-testid={`suggest-fix-${o.order_code}`} title="Suggest a conflict-free reschedule">
              <Wand2 className="h-3.5 w-3.5" /> Fix
            </button>
          )}
          <button onClick={() => onEdit(o)} className={`h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-blue-100 hover:text-blue-700 transition-colors ${hideEdit ? "hidden" : ""}`} data-testid={`edit-order-${o.order_code}`} title="Edit / reschedule">
            <Pencil className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(o)} className="h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-red-100 hover:text-red-700 transition-colors" data-testid={`delete-order-${o.order_code}`} title="Delete">
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      ) : (
        !hasStages && <span className="text-[10px] text-slate-300 italic">read-only</span>
      )}
    </div>
  );
}

export function ProductionRow({ order: o, line, editable, canManage, expanded, onToggle, onEdit, onDelete, onSuggestFix, onLogStage, onShipStage, onReceiveStage, onHistory, highlight = false, hideEdit = false, onOpenOrder, spotlightOverdue = false, isAdmin = false, currentUsername, onApplyBomRefresh, onAcknowledgeBom }) {
  const t = useT();
  const tn = useTn();
  const s = STATUS_STYLES[o.status] || STATUS_STYLES.Pending;
  const overdue = spotlightOverdue && isOverdue(o);
  // "Urgent" = High priority, not yet overdue and still open — amber tint mirrors the Work Orders design.
  const urgent = spotlightOverdue && !overdue && o.priority === "High" && !["Completed", "Cancelled"].includes(o.status);
  const hasStages = (o.stages || []).length > 0;
  const rowRef = useRef(null);
  useEffect(() => {
    if (highlight && rowRef.current) rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlight]);
  return (
    <>
      <tr ref={rowRef}
        onClick={hasStages ? () => onToggle(o.id) : undefined}
        title={hasStages ? "Click anywhere on the row to expand the WIP pipeline" : undefined}
        className={`transition-all duration-500 ${hasStages ? "cursor-pointer" : ""} ${overdue ? "bg-red-50/80 hover:bg-red-50" : urgent ? "bg-amber-50/70 hover:bg-amber-50" : s.row} ${highlight ? "ring-2 ring-inset ring-indigo-400 bg-indigo-50/60" : ""}`} data-testid="production-table-row" data-overdue={String(overdue)}>
        <td className="px-4 py-3 align-top">
          <div className="font-mono-data text-xs font-semibold text-slate-700">{o.order_code}</div>
          {o.bom_outdated && (
            <span className="mt-0.5 inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700" data-testid={`bom-outdated-tag-${o.order_code}`} title="BOM revised — refresh required">
              <AlertTriangle className="h-2.5 w-2.5" /> BOM Revised
            </span>
          )}
          {o.ack_required && !(o.acknowledged_by || []).some((a) => a.by === currentUsername) && (
            <span className="mt-0.5 inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700" data-testid={`bom-ack-tag-${o.order_code}`} title="New production stages — acknowledgement required">
              New Steps
            </span>
          )}
          {o.so_number && (
            <span className="mt-0.5 inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" data-testid={`waiting-on-tag-${o.order_code}`} title={`This job feeds sales order ${o.so_number}${o.customer_name ? ` (${o.customer_name})` : ""}`}>
              <ShoppingBag className="h-2.5 w-2.5" /> Waiting on {o.so_number}{o.customer_name ? ` · ${o.customer_name}` : ""}
            </span>
          )}
          {onOpenOrder && (o.source_orders?.length || o.source_order_code) ? (
            <div className="mt-0.5" onClick={(e) => e.stopPropagation()}><SourceBadge wo={o} onOpenOrder={onOpenOrder} /></div>
          ) : null}
        </td>
        <td className="px-4 py-3 font-medium text-slate-800 align-top"><TranslatedName text={o.product_name} /></td>
        <td className="px-4 py-3 text-right font-mono-data text-slate-700 align-top">{fmtQty(o.target_quantity, o.unit)}</td>
        <ProgressCell order={o} />
        <td className="px-4 py-3">
          <div className="text-slate-700">{fmtDate(o.due_date)}</div>
          {o.estimated_completion && (
            <div className="flex items-center gap-1 text-[11px] text-slate-400 font-mono-data">
              <Clock className="h-3 w-3" />
              {fmtDateTime(o.estimated_completion)}
            </div>
          )}
        </td>
        <td className="px-4 py-3">
          <span className={`text-[10px] font-mono-data uppercase tracking-wide px-2 py-0.5 rounded ${PRIORITY_STYLES[o.priority]}`}>{o.priority}</span>
        </td>
        <td className="px-4 py-3 text-xs text-slate-500 font-mono-data align-top">
          <span className="flex items-center gap-1.5">
            {o.line_name ? <TranslatedName text={o.line_name} /> : t("prod.unassigned")}
            {o.has_conflict && (
              <span title={o.conflict_reason} data-testid={`conflict-badge-${o.order_code}`}>
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              </span>
            )}
          </span>
          {line && <OperatorChips operators={line.operators} className="mt-0.5 font-sans" testid={`line-operators-${o.order_code}`} />}
        </td>
        <StageCell order={o} />
        <td className="px-4 py-3">
          <span className={`text-[10px] font-mono-data uppercase tracking-wide px-2 py-1 rounded ${s.badge}`}>{o.status}</span>
        </td>
        <td className="px-4 py-3">
          <RowActions order={o} editable={editable} expanded={expanded} onToggle={onToggle} onEdit={onEdit} onDelete={onDelete} onSuggestFix={onSuggestFix} hideEdit={hideEdit} />
        </td>
      </tr>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.tr key="stage-row" className="bg-slate-50/70" data-testid={`stage-row-${o.order_code}`}>
            <td colSpan={COLS} className="p-0">
              <motion.div
                key="stage-panel"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={SLIDE}
                className="overflow-hidden"
                data-testid={`stage-panel-${o.order_code}`}
              >
                <div className="px-6 py-4 space-y-4">
                  {onApplyBomRefresh && <BomRevisionBanner order={o} isAdmin={isAdmin} onApply={onApplyBomRefresh} />}
                  {onAcknowledgeBom && <BomAckBanner order={o} currentUsername={currentUsername} onAck={onAcknowledgeBom} />}
                  {(o.stages || []).length > 0 && (
                    <div data-testid={`wip-inline-${o.order_code}`}>
                      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">WIP Pipeline · quantity flow</p>
                      <OrderFlowBar order={o} />
                    </div>
                  )}
                  <StageCards order={o} canManage={canManage} onLog={onLogStage} onShip={onShipStage} onReceive={onReceiveStage} onHistory={onHistory} />
                </div>
              </motion.div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
}
